import {jwt, memoize, qs} from '@api'
import {FilterGroupState} from '@modules'
import {apolloStore, CatalogStore, deployments, DetailsViewFieldConfig, FieldTypes, globalStores, ICatalogFieldConfig, PLM_ENUM_LISTS, SeasonSelectorFieldConfig} from '@stores'
import {IC8DynamicConfig} from '@stores/dev/IC8DynamicConfig'
import {esClient} from '@stores/elasticsearch'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {IPlanDefinition} from '@stores/vlp'
import {action, computed, observable, reaction, runInAction, when} from 'mobx'
import {_} from '../../shared/components/3rd-party'
import { C8ServicesStore } from './C8ServicesStore'
import { GET_C8_CATALOG_ITEM } from './graphql'
import { FetchPolicy } from 'apollo-client'

export interface IEnumValue {
  id?: string
  name: string
  description: string
}

export interface ILookupItem {
  id: string
  name: string
}

export interface IEnumItem {
  name: string, 
  id: string, 
  values: Array<IEnumValue>
}

export class C8Store {
  constructor(public catalog: CatalogStore) {
    synchronizeStores(async () => {
      if (catalog.hasLoadedConfig) {
        const {catalogConfig: {fields}} = catalog,
          enums = _.uniqBy(fields.filter(f => f?.def?.enumType != null), f => f.def.enumType)

        when(() => deployments.hasLoadedDynamicConfig, async () => {
          try {
            await Promise.all(enums.map(({def: {enumType}}) => this.getEnumValuesC8(enumType)))
          } finally {
            this.hasLoadedEnumValues = true
          }
        })
      }
      else {
        this.hasLoadedEnumValues = true
      }
    })
  }

  get vlp() { return this.catalog.vlp}
  get config() { return this.stores.config}
  get stores() { return this.catalog.stores }
  @computed get hasLoadedHierarchy() {
    const {workspaceConfig, vlp} = this.catalog
    return ((workspaceConfig?.seasonSelectors ?? []).some(f => f.type == 'seasonal') ? true : true) &&
           ((workspaceConfig?.seasonSelectors ?? []).some(f => f.fields.some(f => f.field == 'plan')) ? !vlp ? true : vlp.hierarchy.hasLoadedHierarchy : true)
  }

  @computed get baseUrl() { return this.config.config.centricURL }

  @computed get plmC8URL(){return this.config.config.plmC8URL }
  
  @computed get baseHomeUrl() { return `${this.config.config.centricURL}/WebAccess/home.html` }

  get sso() { return this.stores.ui.settings.c8.sso}

  @computed get boardC8Url() {
    const {sso, vlp: {hierarchy: {selected: {collections, folders, plans}}}, catalog: {seasonSelectorFields,catalogConfig: {redirectToPLM}, filter: {state: {groups}}}} = this,
      {tokenContents} = this

    const mostSpecificFilter = _.findLast<FilterGroupState>(seasonSelectorFields.map(f => groups[f.field]), f => f?.selected?.size == 1)
    const c8Id = mostSpecificFilter ? Array.from<{exId?}>(mostSpecificFilter.selected.values())[0]?.exId : (_.first(collections) ?? _.last(_.first(folders)) ?? _.first(plans))

    if (redirectToPLM){
      const regex = /^C\d\^/i;
      const exURL = c8Id ? c8Id.replace(regex, '') : null
      return !this.plmC8URL ? null : `${this.plmC8URL}/WebAccess/home.html#URL=${exURL ??  'centric%3A%2F%2FSITEADMIN%2F_CS_Site&RURL'}`
    }

    return !tokenContents
           ? null
          : `${this.baseUrl}/WebAccess/home.html#URL=${c8Id ?? /*seasonSelectorsFields.map(f => f).find(_.identity) ??*/ 'centric%3A%2F%2FSITEADMIN%2F_CS_Site&RURL'}&Tab=Configuration&Tab1=Business%20Objects${sso ? `&SessionURL=${tokenContents.sessionUrl}` : ''}`
  }

  @computed get tokenContents() : C8TokenContents {
    return jwt.decode(this.stores.authStore.accessToken) as C8TokenContents
  }

  @observable hasLoadedEnumValues = false

  urlForTab = () => {
    const {sso, tokenContents, baseUrl} = this

    // This is for Merchandising hierarchy. For dev hierarchy we need to handle the case.
    return !tokenContents ? null
                          : `${baseUrl}/WebAccess/home.html#URL=centric%3A%2F%2FREFLECTION%2FINSTANCE%2F_CS_Data%2Fnull&Tab=Merchandising${sso ? `&SessionURL=${tokenContents.sessionUrl}` : ''}`
  }

  urlForId = (exId: string, redirectToPLM:boolean=false) => {
    const {sso, tokenContents, baseUrl, plmC8URL} = this
    if (redirectToPLM){
      return !plmC8URL ? null : `${plmC8URL}/WebAccess/home.html#URL=${exId}`
    }
    return !tokenContents ? null
                          : `${baseUrl}/WebAccess/home.html#URL=${exId}&Tab=Configuration&Tab1=Business%20Object&${sso ? `&SessionURL=${tokenContents.sessionUrl}` : ''}`
  }
  
  openTabFor = (field: string) => {
    const {sso, tokenContents, baseHomeUrl: homeUrl} = this
    let url
    switch(field) {
      case FieldTypes.collectionVersion : {
        const {vlp: {hierarchy: {selectedPlans, plans: {plans}}}} = this,
        planId = _.first<IPlanDefinition>(selectedPlans)?.id || _.first<IPlanDefinition>(plans)?.id
        if (planId) url = `${homeUrl}#URL=${planId}&RURL=&Tab=Versions${sso ? `&SessionURL=${tokenContents.sessionUrl}` : ''}`
        break
      }
      default: console.error(`Provide details for ${field} addItem`)
    }
    url && window.open(url, '_blank')
  }

  @observable.ref services: C8ServicesStore = new C8ServicesStore(this)
  
  imageUrlKeyFor = (rawImageUrl: string) => {
    const parsed = this.parseImageUrl(rawImageUrl)
    return !_.isEmpty(parsed) ? {key: `${parsed.URL}|${parsed.Attribute}`, parsed } : {}
  }

  parseImageUrl = (rawImageUrl: string) => {
    // http://gant-mirror6.centricsoftware.com/csi-requesthandler/RequestHandler?URL=C10450813&Download=false&Attribute=Thumbnail&Module=Publisher&Operation=GetFromNode&SecurityTokenURL=centric://_CS_SecurityToken/620ce010ac1068112af132a3d3869689
    const parsed = qs.parseUrl(rawImageUrl)

    return _.pick(parsed.query, ['URL', 'Attribute'])
  }

  @observable enumByName = observable.map<string, IEnumValue[]>({}, {deep: false})
  @observable lookuItemBySubtype = observable.map<string, ILookupItem[]>({}, {deep: false})
  
  @action fetchPlmEnumList = async (whereClause) => {
    const {data: {plmEnumLists}} = await this.stores.apollo.client.query({
      query: PLM_ENUM_LISTS,
      variables: {
        where: {
          ...whereClause
        }
      },
      fetchPolicy: 'network-only'
    })
    return plmEnumLists?.items ?? []
  }

  @action fetchAllEnumValuesC8 = async () => {
    const {enumByName} = this,
      {enumFields} = this.catalog

    if (deployments.c8DynamicConfig) {
      enumByName.replace(deployments.enumByName)
    }
    else { // Deprecated - get from grapqhl
      const where = enumFields.map(f => ({name: f?.def?.enumType})),
        items: Array<IEnumItem> = await this.fetchPlmEnumList({OR: [...where]})

      runInAction(`Save enum values from C8`, () => {
        if (items?.length) {
          _.forEach(items, (item) => {
            const {values, name} = item
            enumByName.set(name, values)
          })
        }
      })
    }
  }

  @action getEnumValues = async ({def}: Partial<ICatalogFieldConfig> | Partial<DetailsViewFieldConfig>) : Promise<Array<IEnumValue>> => {
    const {enumValues, enumType} = def ?? {}

    if (_.isArray(enumValues)) return enumValues.map(v => ({name: v, description: v}))

    if (enumType) {
      return await this.getEnumValuesC8(enumType)
    }
  }

  @action getEnumValuesC8 = async (name: string) : Promise<Array<IEnumValue>> => {
    const {enumByName} = this
    if (enumByName.has(name)) {
      return enumByName.get(name)
    }

    const {c8DynamicConfig} = deployments

    if (c8DynamicConfig) {
      const values = deployments.enumByName[name]
      enumByName.set(name, values)
      return values
    }

    // console.log(`fetchPlmEnumList(${name})`)
    let finalValues = [],
    items: Array<IEnumItem> = await this.fetchPlmEnumList({name})

    return runInAction(`Save enum values for '${name}'`, () => {
      if (items?.length) {
        const [{values}] = items
        finalValues = values
      }
      // console.log(data?.data?.plmEnumLists?.items[0]?.values)
      enumByName.set(name, finalValues)
      return finalValues
    })
  }

  @action getAllEnumValues = async () => {
    const {catalog: {enumFields}, getEnumValues} = this
    await Promise.all(enumFields.map(f => {
      const res = getEnumValues(f)
      return res
    }))
}

  @action getAllLookupItems = async () => {
    const {catalog: {lookupItemFields}, getC8LookupItemBySubtype} = this
    await Promise.all(lookupItemFields.map(f => {
      const res = getC8LookupItemBySubtype(f?.def?.subType)
      return res
    }))
  }

  @action reset = () => {
    this.services.reset()
  }

  @action getC8Item = async (exId: string, fetchPolicy: FetchPolicy = 'cache-first') => {
    try {
      const r = await apolloStore.client.query<{ getC8CatalogItem?: { node?: any } }>({
        query: GET_C8_CATALOG_ITEM,
        variables: {exId},
        fetchPolicy
      })

      return r?.data?.getC8CatalogItem?.node
    }
    catch (err) {
      console.error(err)
    }

    return null
  }

  @action getC8LookupItemBySubtype = async (subtype: string): Promise<Array<ILookupItem>> => {
    const {axiosNest} = esClient
    const  {lookuItemBySubtype}  = this
    if (axiosNest) {
      try {
        if (lookuItemBySubtype.has(subtype)) {
          return lookuItemBySubtype.get(subtype)
        }
        const {data} = await esClient.axiosNest.get<Array<{ id: string, name: string }>>(`/c8/lookupItem?subtype=${subtype}`)
        lookuItemBySubtype.set(subtype, data)
        return data
      } catch (err) {
        console.error(err)
      }
    }
  }
}

export interface C8TokenContents {
  sessionUrl: string
}
function wait(arg0: () => any) {
    throw new Error('Function not implemented.')
}




// WEBPACK FOOTER //
// ./src/stores/c8/C8Store.ts